import { useQuery } from '@tanstack/react-query';
import type { FrameRecommendationPage } from '@optiflow/types';
import { api } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';
import { recommendationParams, type AppliedRecommendation } from '../criteria';

export const frameRecommendationKeys = {
  all: ['frame-recommendations'] as const,
  forCustomer: (customerId: string) =>
    [...frameRecommendationKeys.all, 'customer', customerId] as const,
  page: (customerId: string, branchId: string | null, applied: AppliedRecommendation) =>
    [...frameRecommendationKeys.forCustomer(customerId), branchId, applied] as const,
};

/**
 * One page of the server's ranking for a customer.
 *
 * The server ranks every eligible frame and this asks only for the page on screen, so nothing is scored, filtered or reordered here. Stock arrives already decided by what the user may see; no other endpoint is asked about it.
 */
export function useFrameRecommendationsQuery(
  customerId: string | undefined,
  applied: AppliedRecommendation,
) {
  const branchId = useAuthStore((state) => state.activeBranchId);

  return useQuery({
    queryKey: frameRecommendationKeys.page(customerId ?? '', branchId, applied),
    queryFn: () =>
      api.get<FrameRecommendationPage>('/frame-recommendations', {
        params: recommendationParams({ customerId: customerId ?? '', ...applied }),
      }),
    enabled: Boolean(customerId),
    // The current results stay on screen while new criteria load — but never across a customer or a branch, where they would describe someone else's stock.
    placeholderData: (previous, previousQuery) => {
      const key = previousQuery?.queryKey;
      return key && key[2] === customerId && key[3] === branchId ? previous : undefined;
    },
  });
}
